import {
  decideInviteAddress, MAX_INVITE_ADDRESSES, splitInviteList, type AddressRefusal,
} from '../../core/invitations';
import type { ApiResult, CreatedInvitation, EmailResult, InvitationOutcome } from '../api';
import InviteLink from './InviteLink';

/**
 * One answer per address (R2), shared by the invite panel, the new-group flow and the panel a
 * new group lands with.
 *
 * Three screens send invitations and all three have to say the same thing about each address:
 * emailed, link only and why, already a member, skipped and why, or not created and retryable.
 * Written once here because three copies of that vocabulary is how one screen ends up calling
 * a failed email "emailed" (R6) — the one mistake this feature exists not to make.
 *
 * The functions above the component are the functional core of a bulk invite: parsing a paste
 * into what will be sent and what will not, turning one response into one row, and running the
 * requests a few at a time. None of them renders or reads a clock.
 */

/** Why one address got no invitation. A code, not a sentence — the copy is chosen below. */
export type InviteFailure = 'network' | 'refused' | 'rate_limited';

export type InviteResult =
  /** Never sent: the paste itself was the problem. Shown before any request (AE1). */
  | { email: string; state: 'skipped'; reason: AddressRefusal }
  /** In flight. */
  | { email: string; state: 'inviting' }
  | { email: string; state: 'invited'; emailResult: EmailResult; invitation: CreatedInvitation }
  /** The goal is already true. Not a failure, and nothing to retry. */
  | { email: string; state: 'already_member' }
  | { email: string; state: 'failed'; reason: InviteFailure };

export interface SkippedAddress {
  /** Exactly as it was typed, so the admin can find it in what they pasted. */
  email: string;
  reason: AddressRefusal;
}

export interface InvitePlan {
  /** Normalized, deduplicated, at most `MAX_INVITE_ADDRESSES`, in the order they were typed. */
  emails: string[];
  skipped: SkippedAddress[];
}

/**
 * A pasted list, judged address by address with the same rule the new-group flow uses (R1).
 *
 * Each accepted address joins `existing` before the next is judged, so a repeat later in the
 * paste is a duplicate and the twenty-fifth is `list_full`. Pure: the Worker re-decides every
 * address anyway, and this exists so nobody waits on a request to learn about a typo.
 */
export function planInvites(raw: string, self: string | null, existing: readonly string[] = []): InvitePlan {
  const emails = [...existing];
  const skipped: SkippedAddress[] = [];
  for (const entry of splitInviteList(raw)) {
    const decision = decideInviteAddress({ raw: entry, existing: emails, self });
    if (decision.added) emails.push(decision.email);
    else skipped.push({ email: entry, reason: decision.reason });
  }
  return { emails: emails.slice(existing.length), skipped };
}

/** One response from the create or resend route, as the row that reports it. */
export function settleInvite(email: string, result: ApiResult<InvitationOutcome>): InviteResult {
  if (result.ok) {
    return result.data.result === 'invited'
      ? { email, state: 'invited', emailResult: result.data.emailResult, invitation: result.data.invitation }
      : { email, state: 'already_member' };
  }
  if (result.status === 429) return { email, state: 'failed', reason: 'rate_limited' };
  return { email, state: 'failed', reason: result.status === 0 ? 'network' : 'refused' };
}

/** Four in flight (KTD4): 24 addresses is six rounds of the 5 s mail timeout at worst. */
export const INVITE_CONCURRENCY = 4;

/**
 * Run `task` over `items`, at most `limit` at a time, starting them in order.
 *
 * Each task reports its own row, so nothing here collects results. A task that throws is
 * swallowed rather than allowed to reject the batch: one address failing must never stop the
 * ones behind it (R2). `call` does not throw, so this is a backstop, not a path.
 */
export async function fanOut<T>(
  items: readonly T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next] as T;
      next += 1;
      try { await task(item); } catch { /* the task owns its row; see above */ }
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

export const SKIPPED_COPY: Readonly<Record<AddressRefusal, string>> = {
  invalid: 'Skipped: not an email address',
  self: 'Skipped: that is your own address',
  duplicate: 'Skipped: already on this list',
  list_full: `Skipped: ${MAX_INVITE_ADDRESSES} addresses is as many as one list can invite`,
};

const FAILURE_COPY: Readonly<Record<InviteFailure, string>> = {
  network: 'We could not reach Collab for this one.',
  refused: 'Collab would not create this invitation.',
  rate_limited: 'Too many invitations just now — try this one again in a minute',
};

export interface InviteResultListProps {
  results: readonly InviteResult[];
  /** Absent where a retry makes no sense. Present, it is offered on every failed row. */
  onRetry?: (email: string) => void;
  /** Disables the retry buttons while anything is in flight. */
  busy?: boolean;
}

/**
 * The rows. Every caller renders this inside `.collab-invite-dialog`, a replay block selector:
 * the rows are addresses, and an invited row is a live link.
 */
export function InviteResultList({ results, onRetry, busy = false }: InviteResultListProps) {
  if (results.length === 0) return null;
  return (
    <ul className="address-list invite-results">
      {results.map((result, index) => (
        // Index in the key: a skipped row is keyed by what was typed, which can repeat.
        <li key={`${index}:${result.email}`} className={`invite-result is-${result.state}`}>
          {result.state === 'invited' ? (
            // Keyed by the invitation, so a fresh link resets the "Copied." beside the old one.
            <InviteLink key={result.invitation.id} invitation={result.invitation} emailResult={result.emailResult} />
          ) : (
            <>
              <span>{result.email}</span>
              {result.state === 'failed' && onRetry && (
                // "Invite again" on screen, the address in the accessible name: a paste of
                // twenty can fail several rows, and they have to be told apart by ear.
                <button
                  type="button"
                  aria-label={`Invite again: ${result.email}`}
                  disabled={busy}
                  onClick={() => onRetry(result.email)}
                >
                  Invite again
                </button>
              )}
              <p className="hint">
                {result.state === 'inviting' && 'Inviting…'}
                {result.state === 'already_member' && 'Already a member of this group.'}
                {result.state === 'skipped' && SKIPPED_COPY[result.reason]}
                {result.state === 'failed' && FAILURE_COPY[result.reason]}
              </p>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}
